import { Injectable } from '@angular/core';
import { Observable, map, Subject } from 'rxjs';
import { ApiResponse } from 'src/app/shared/model/ApiResponse';
import { ApiConstants } from 'src/app/core/endpointsAndGeneric-constants/api-constants';
import { HttpMethodModifierService } from 'src/app/core/http-method-modifier.service';
import { TemplatesResponse } from '../../batch-management/model/TemplatesResponse';
import { TemplateAdapter } from '../../template-management-v1/adapter/TemplateAdapter';
import { TemplateConfigurationAdapter } from '../../template-management-v1/adapter/TemplateConfigurationAdapter';
import { HttpParams } from '@angular/common/http';
import { Behaviour, Payload, SimulationData } from 'src/app/core/model';
import { SharedService } from 'src/app/shared/common-services/shared.service';

@Injectable({
  providedIn: 'root'
})
export class TemplateManagementService {

  private _transformedPayload;
  private _addTemplateDetails = {
    'templateName': null,
    'description': null,
  };
  private _rawPayload: object;

  stepperProgress = {
    'add-template': false,
    'edit-template': false,
    'customize-fields': false
  };
  stepperProgressChange: Subject<any> = new Subject<any>();

  private _selectedTenantId: string = null;

  constructor(
    private httpGuard: HttpMethodModifierService,
    private _sharedService: SharedService
  ) { 
    this.stepperProgressChange.subscribe((value) =>{
      this.stepperProgress[value.pageType] = value.value;
    });
  }

  /**
   * Creates template
   * @param payload 
   * @returns template 
   */
  createTemplate(payload: Payload) {
    return this.httpGuard.httpReq('post', 'API_ENDPOINT', ApiConstants.URL.templateV2, payload);
  }

  /**
   * Gets templates
   * @param offset 
   * @param limit 
   * @param searchKey 
   * @returns templates 
   */
  getTemplates(offset: number, limit: number, searchKey: string): Observable<ApiResponse<TemplatesResponse>> {
    let httpParams = new HttpParams();
    if (offset) {
      httpParams = httpParams?.set('offset', offset);
    }
    if (limit) {
      httpParams = httpParams?.set('limit', limit);
    }
    if (searchKey) {
      httpParams = httpParams?.set('searchKey', searchKey);
    }
    return this.httpGuard.httpReq('get', 'API_ENDPOINT', ApiConstants.URL.templateV2, httpParams?.toString() ? '?' + httpParams?.toString() : null).pipe(map(data => TemplateAdapter.parseData(data)));
  }

  /**
   * Gets templateby id
   * @param templateId 
   * @returns templateby id 
   */
  getTemplatebyId(templateId: string) {
    const param = '/' + templateId;
    return this.httpGuard.httpReq('get', 'API_ENDPOINT', `${ApiConstants.URL.templateV2}${param}`).pipe(map(data => TemplateConfigurationAdapter.parseData(data)));
  }

  /**
   * Deletes template
   * @param templateId 
   * @returns template 
   */
  deleteTemplate(templateId: string) {
    const param = '/' + templateId;
    return this.httpGuard.httpReq('delete', 'API_ENDPOINT', `${ApiConstants.URL.templateV2}${param}`);
  }

  /**
   * Edits template
   * @param payload 
   * @returns template 
   */
  editTemplate(payload: Payload) {
    return this.httpGuard.httpReq('patch', 'API_ENDPOINT', ApiConstants.URL.templateV2, payload);
  }

  /**
   * Templates mapped or not
   * @param templateId 
   * @returns mapped or not 
   */
  templateMappedOrNot(templateId: string) {
    const param = '/' + templateId;
    return this.httpGuard.httpReq('get', 'API_ENDPOINT', `${ApiConstants.URL.getAttachedBatchV2}${param}`);
  }

  /**
   * Sets transformed payload
   * @param payload 
   */
  setTransformedPayload(payload) {
    this._transformedPayload = payload;
  }

  /**
   * Gets tranformed payload
   * @returns  
   */
  getTranformedPayload() {
    return this._transformedPayload;
  }

  /**
   * Sets add template details
   * @param details 
   * @param selectedTenantId 
   */
  setAddTemplateDetails(details, selectedTenantId) {
    this._addTemplateDetails = details;
    this._selectedTenantId = selectedTenantId;
  }

  /**
   * Gets add template details
   * @returns  
   */
  getAddTemplateDetails() {
    return { details: this._addTemplateDetails, selectedTenant: this._selectedTenantId };
  }

  /**
   * Sets raw payload
   * @param payload 
   */
  setRawPayload(payload) {
    this._rawPayload = payload;
  }

  /**
   * Gets raw payload
   * @returns  
   */
  getRawPayload() {
    return this._rawPayload;
  }

  /**
   * Sets stepper progress
   * @param pageType 
   * @param value 
   */
  setStepperProgress(pageType: string, value: boolean){
    this.stepperProgressChange.next({pageType: pageType, value: value});
  } 

  /**
   * Sets field attributes
   * @param field 
   * @param [type] 
   * @returns  
   */
  setFieldAttributes(field: SimulationData, type?: string) {
    const fieldMappings = this._sharedService.getAttributes('templateConfigurations')?.fieldMappings ? this._sharedService.getAttributes('templateConfigurations')?.fieldMappings : [];
    const fieldMappingEntry = fieldMappings?.find(entry => entry?.type === field?.attributes?.type);
    if (type === 'payload') {
      const attributesObject = fieldMappingEntry
        ? Object.entries(fieldMappingEntry?.fields)?.reduce((mappings, [fieldKey]) => {
          const value = field?.attributes?.[fieldKey];
          if (value!== null) {
            mappings[fieldKey] = value;
          }
          return mappings;
        }, {} as { [key: string]: any })
        : {};
      return attributesObject;
    } else {
      const attributesArray = fieldMappingEntry
        ? Object.entries(fieldMappingEntry?.fields)?.map(([fieldKey, fieldLabel]) => ({
          key: fieldLabel,
          value: (fieldLabel === 'Cumulative' || fieldLabel === 'Carry Forward')
            ? (field?.attributes?.[fieldKey] ? 'Yes' : ((field?.attributes?.[fieldKey] === false) ? 'No' : 'NA'))
            : fieldLabel === 'Behaviour After Last Value' ? (field?.attributes?.[fieldKey] ? Behaviour[field?.attributes?.[fieldKey] as keyof typeof Behaviour] : '--') : (field?.attributes?.[fieldKey] ?? '--'),
          backgroundColorCode: '#005978',
          colorCode: '#FFFFFF'
        }))
        : [];
      return attributesArray;
    }
  }
}
